/**
 * ============================================================
 * 个人资料页面 (Profile)
 * ============================================================
 * 用户可以修改个人信息和密码
 * ============================================================
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../auth';
import { authApi } from '../api';
import { User, Lock, Mail, Phone, Building2, CreditCard, Landmark, IdCard, Save, Shield } from 'lucide-react';

interface ProfileForm {
  name: string;
  name_real: string;
  phone: string;
  email: string;
  card_type: string;
  // 企业
  company_name?: string;
  bank_account_public?: string;
  bank_name?: string;
  // 个人
  bank_card?: string;
  personal_bank_name?: string;
  id_card?: string;
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [profileForm, setProfileForm] = useState<ProfileForm>({
    name: '',
    name_real: '',
    phone: '',
    email: '',
    card_type: '个人',
    company_name: '',
    bank_account_public: '',
    bank_name: '',
    bank_card: '',
    personal_bank_name: '',
    id_card: '',
  });
  // 标记是否完成初始化，避免切换账户类型的 useEffect 清空刚加载的数据
  const [initialized, setInitialized] = useState(false);

  // 当 user 数据加载完成后，更新表单
  useEffect(() => {
    if (user) {
      const isEnterpriseUser = user.card_type === '企业';
      setProfileForm({
        name: user.name || '',
        name_real: user.name_real || '',
        phone: user.phone || '',
        email: user.email || '',
        card_type: user.card_type || '个人',
        // 企业用户字段
        company_name: isEnterpriseUser ? (user.company_name || '') : '',
        bank_account_public: isEnterpriseUser ? (user.bank_account_public || '') : '',
        bank_name: isEnterpriseUser ? (user.bank_name || '') : '',
        // 个人用户字段
        bank_card: isEnterpriseUser ? '' : (user.bank_card || ''),
        personal_bank_name: isEnterpriseUser ? '' : (user.bank_name || ''), // 个人用户开户行也存储在bank_name字段
        id_card: isEnterpriseUser ? '' : (user.id_card || ''),
      });
      // 标记初始化完成
      setInitialized(true);
    }
  }, [user]);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showSuccess = useCallback((m: string) => {
    setToast({ message: m, type: 'success' });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const showError = useCallback((m: string) => {
    setToast({ message: m, type: 'error' });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) {
      showError('显示名称不能为空');
      return;
    }
    setSaving(true);
    try {
      // 构建正确的保存数据，确保个人用户开户行保存到bank_name字段
      const saveData: any = { ...profileForm };
      if (!isEnterprise && profileForm.personal_bank_name) {
        saveData.bank_name = profileForm.personal_bank_name;
      }
      const updatedUser = await authApi.profile(saveData);
      if (updatedUser && setUser) {
        setUser(updatedUser);
      }
      showSuccess('资料更新成功');
    } catch (err: any) {
      showError(err.message || '更新失败');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.oldPassword.trim()) {
      showError('请输入旧密码');
      return;
    }
    if (!passwordForm.newPassword.trim()) {
      showError('请输入新密码');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      showError('新密码至少需要6位');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showError('两次输入的密码不一致');
      return;
    }
    setSaving(true);
    try {
      await authApi.password({
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
      });
      showSuccess('密码修改成功');
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      showError(err.message || '密码修改失败');
    } finally {
      setSaving(false);
    }
  };

  const isEnterprise = profileForm.card_type === '企业';

  // 当账户类型改变时，重置相关字段（仅在初始化完成后）
  useEffect(() => {
    // 只有在初始化完成后，用户主动切换账户类型时才重置字段
    if (!initialized) return;
    
    if (isEnterprise) {
      setProfileForm(prev => ({
        ...prev,
        bank_card: '',
        personal_bank_name: '',
        id_card: '',
      }));
    } else {
      setProfileForm(prev => ({
        ...prev,
        company_name: '',
        bank_account_public: '',
        bank_name: '',
      }));
    }
  }, [profileForm.card_type, initialized]);

  return (
    <div className="max-w-2xl mx-auto">
      {/* 页面标题 */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
          <User className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold">个人资料</h1>
          <p className="text-sm text-[#94A3B8]">管理您的账户信息</p>
        </div>
      </div>

      {/* 标签切换 */}
      <div className="flex gap-2 mb-6 bg-[#1E293B] rounded-lg p-1">
        <button
          onClick={() => setActiveTab('profile')}
          className={`flex-1 h-10 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'profile'
              ? 'bg-[#3B82F6] text-white'
              : 'text-[#94A3B8] hover:text-[#F1F5F9]'
          }`}
        >
          <User className="w-4 h-4" /> 基本信息
        </button>
        <button
          onClick={() => setActiveTab('password')}
          className={`flex-1 h-10 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'password'
              ? 'bg-[#3B82F6] text-white'
              : 'text-[#94A3B8] hover:text-[#F1F5F9]'
          }`}
        >
          <Lock className="w-4 h-4" /> 修改密码
        </button>
      </div>

      {/* 内容区域 */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6">
        {activeTab === 'profile' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">登录账号</label>
                <div className="h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] flex items-center">
                  {user?.username || '—'}
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">账户类型</label>
                <select
                  value={profileForm.card_type}
                  onChange={(e) => setProfileForm({ ...profileForm, card_type: e.target.value })}
                  className={`w-full h-10 px-4 rounded-lg text-sm flex items-center focus:outline-none ${
                    profileForm.card_type === '企业'
                      ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30 focus:border-[#F59E0B]'
                      : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 focus:border-[#10B981]'
                  }`}
                >
                  <option value="个人" className="bg-[#1E293B]">个人</option>
                  <option value="企业" className="bg-[#1E293B]">企业</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">显示名称</label>
              <input
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] focus:outline-none focus:border-[#3B82F6]"
                placeholder="显示名称"
              />
            </div>

            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">{isEnterprise ? '联系人姓名' : '真实姓名'}</label>
              <input
                value={profileForm.name_real}
                onChange={(e) => setProfileForm({ ...profileForm, name_real: e.target.value })}
                className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] focus:outline-none focus:border-[#3B82F6]"
                placeholder={isEnterprise ? '联系人姓名' : '真实姓名'}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">联系电话</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <input
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="w-full h-10 pl-10 pr-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] focus:outline-none focus:border-[#3B82F6]"
                    placeholder="联系电话"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">邮箱</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <input
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    className="w-full h-10 pl-10 pr-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] focus:outline-none focus:border-[#3B82F6]"
                    placeholder="邮箱地址"
                  />
                </div>
              </div>
            </div>

            {/* 企业信息 - 可编辑 */}
            {isEnterprise && (
              <div className="pt-4 border-t border-[#334155] space-y-4">
                <h4 className="text-sm font-medium text-amber-400 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> 企业信息
                </h4>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">企业名称</label>
                  <input
                    value={profileForm.company_name || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
                    className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] focus:outline-none focus:border-[#F59E0B]"
                    placeholder="企业名称"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-2">对公账户</label>
                    <input
                      value={profileForm.bank_account_public || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, bank_account_public: e.target.value })}
                      className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] font-mono focus:outline-none focus:border-[#F59E0B]"
                      placeholder="对公账户"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-2">开户行</label>
                    <input
                      value={profileForm.bank_name || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, bank_name: e.target.value })}
                      className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] focus:outline-none focus:border-[#F59E0B]"
                      placeholder="开户行"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 个人信息 - 可编辑 */}
            {!isEnterprise && (
              <div className="pt-4 border-t border-[#334155] space-y-4">
                <h4 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
                  <User className="w-4 h-4" /> 个人信息
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-2">身份证号</label>
                    <input
                      value={profileForm.id_card || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, id_card: e.target.value })}
                      className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] font-mono focus:outline-none focus:border-[#10B981]"
                      placeholder="身份证号"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-2">开户行</label>
                    <input
                      value={profileForm.personal_bank_name || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, personal_bank_name: e.target.value })}
                      className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] focus:outline-none focus:border-[#10B981]"
                      placeholder="开户行"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">银行卡号</label>
                  <input
                    value={profileForm.bank_card || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, bank_card: e.target.value })}
                    className="w-full h-10 px-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] font-mono focus:outline-none focus:border-[#10B981]"
                    placeholder="银行卡号"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex-1 h-11 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? '保存中...' : '保存修改'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'password' && (
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-[#94A3B8]">
                  <p className="text-blue-400 font-medium mb-1">安全提示</p>
                  <p>修改密码需要验证当前密码，请确保您记得原密码。新密码建议至少6位，包含字母和数字。</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">当前密码 <span className="text-red-400">*</span></label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <input
                  type="password"
                  value={passwordForm.oldPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                  className="w-full h-10 pl-10 pr-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6]"
                  placeholder="请输入当前密码"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">新密码 <span className="text-red-400">*</span></label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="w-full h-10 pl-10 pr-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6]"
                  placeholder="请输入新密码（至少6位）"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">确认新密码 <span className="text-red-400">*</span></label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full h-10 pl-10 pr-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6]"
                  placeholder="请再次输入新密码"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleChangePassword}
                disabled={saving}
                className="flex-1 h-11 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? '修改中...' : '修改密码'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toast 提示 */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50 ${
            toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'
          } text-white`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}